import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  HardHat,
  RefreshCw,
  FileSpreadsheet,
  FileText,
  Wallet,
  Package,
  Receipt,
  Clock,
} from "lucide-react";
import {
  getProjectsReport,
  generateReportPdf,
  type ProjectsReport,
} from "@/utils/reports.functions";
import { downloadCsv, downloadPdfFromBase64 } from "@/lib/report-export";
import { useLocaleFormat } from "@/lib/i18n-format";
import { PdfPreviewDialog } from "@/components/reports/PdfPreviewDialog";

export const Route = createFileRoute("/_app/reports/projects")({
  component: ProjectsReportPage,
});

function todayPlusYearISO() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

// Período fixo "todo o histórico" — sem filtro de data na UI
const PERIOD_START = "2000-01-01";

function ProjectsReportPage() {
  const { t } = useTranslation();
  const { company } = useAuth();
  const { formatMoney, formatNumber } = useLocaleFormat();
  const currency = company?.currency ?? "USD";

  const periodStart = PERIOD_START;
  const periodEnd = todayPlusYearISO();
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [contractFilter, setContractFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [data, setData] = useState<ProjectsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<"csv" | "pdf" | null>(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewBase64, setPdfPreviewBase64] = useState<string | null>(null);
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState<string>("");
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
  const [contracts, setContracts] = useState<
    Array<{ id: string; number: string; title: string; client_id: string | null }>
  >([]);
  const [projects, setProjects] = useState<
    Array<{
      id: string;
      name: string;
      code: string;
      client_id: string | null;
      contract_id: string | null;
    }>
  >([]);

  const reportFn = useServerFn(getProjectsReport);
  const pdfFn = useServerFn(generateReportPdf);

  useEffect(() => {
    if (!company?.id) return;
    void supabase
      .from("clients")
      .select("id, name")
      .eq("company_id", company.id)
      .eq("is_active", true)
      .order("name")
      .then(({ data }) => setClients((data ?? []) as any));
    void supabase
      .from("contracts")
      .select("id, number, title, client_id")
      .eq("company_id", company.id)
      .order("number", { ascending: false })
      .then(({ data }) => setContracts((data ?? []) as any));
    void supabase
      .from("projects")
      .select("id, name, code, client_id, contract_id")
      .eq("company_id", company.id)
      .order("name")
      .then(({ data }) => setProjects((data ?? []) as any));
  }, [company?.id]);

  // Reset cascading filters
  useEffect(() => {
    if (clientFilter !== "all" && contractFilter !== "all") {
      const c = contracts.find((x) => x.id === contractFilter);
      if (c && c.client_id && c.client_id !== clientFilter) setContractFilter("all");
    }
    if (clientFilter !== "all" && projectFilter !== "all") {
      const p = projects.find((x) => x.id === projectFilter);
      if (p && p.client_id && p.client_id !== clientFilter) setProjectFilter("all");
    }
    if (contractFilter !== "all" && projectFilter !== "all") {
      const p = projects.find((x) => x.id === projectFilter);
      if (p && p.contract_id && p.contract_id !== contractFilter) setProjectFilter("all");
    }
  }, [clientFilter, contractFilter, projectFilter, contracts, projects]);

  const visibleContracts =
    clientFilter === "all" ? contracts : contracts.filter((c) => c.client_id === clientFilter);

  const filteredProjects = projects.filter((p) => {
    if (clientFilter !== "all" && p.client_id !== clientFilter) return false;
    if (contractFilter !== "all" && p.contract_id !== contractFilter) return false;
    return true;
  });

  const load = async () => {
    if (!periodStart || !periodEnd) return;
    setLoading(true);
    try {
      const r = await reportFn({
        data: {
          periodStart,
          periodEnd,
          projectId: projectFilter === "all" ? null : projectFilter,
          contractId: contractFilter === "all" ? null : contractFilter,
          clientId: clientFilter === "all" ? null : clientFilter,
        },
      });
      setData(r);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.reports.common.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFilter, contractFilter, clientFilter]);

  const handleExportCsv = () => {
    if (!data) return;
    setExporting("csv");
    try {
      const headers = [
        t("pages.reports.projects.csvHeaders.code"),
        t("pages.reports.projects.csvHeaders.project"),
        t("pages.reports.projects.csvHeaders.client"),
        t("pages.reports.projects.csvHeaders.status"),
        t("pages.reports.projects.csvHeaders.budget"),
        t("pages.reports.projects.csvHeaders.hours"),
        t("pages.reports.projects.csvHeaders.labor"),
        t("pages.reports.projects.csvHeaders.materials"),
        t("pages.reports.projects.csvHeaders.expenses"),
        t("pages.reports.projects.csvHeaders.totalCost"),
        t("pages.reports.projects.csvHeaders.variance"),
        t("pages.reports.projects.csvHeaders.utilization"),
      ];
      const rows = data.rows.map((r) => [
        r.code,
        r.name,
        r.client ?? "",
        r.status,
        r.budget,
        r.laborHours,
        r.laborCost,
        r.materialsCost,
        r.expensesCost,
        r.totalCost,
        r.variance,
        (r.utilization * 100).toFixed(1),
      ]);
      downloadCsv(
        `${t("pages.reports.projects.csvFilenamePrefix")}-${periodStart}-${periodEnd}.csv`,
        headers,
        rows,
      );
    } finally {
      setExporting(null);
    }
  };

  const handleExportPdf = async () => {
    if (!periodStart || !periodEnd) {
      toast.error(t("pages.reports.common.loadError"));
      return;
    }
    setExporting("pdf");
    try {
      const { pdfBase64, filename } = await pdfFn({
        data: {
          kind: "projects",
          periodStart,
          periodEnd,
          projectId: projectFilter === "all" ? null : projectFilter,
          contractId: contractFilter === "all" ? null : contractFilter,
          clientId: clientFilter === "all" ? null : clientFilter,
        },
      });
      setPdfPreviewBase64(pdfBase64);
      setPdfPreviewFilename(filename);
      setPdfPreviewOpen(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.reports.common.pdfError"));
    } finally {
      setExporting(null);
    }
  };

  const confirmDownloadPdf = () => {
    if (!pdfPreviewBase64) return;
    downloadPdfFromBase64(pdfPreviewFilename, pdfPreviewBase64);
    setPdfPreviewOpen(false);
  };

  const totals = data?.totals;
  const totalUtilization =
    totals && totals.budget > 0 ? (totals.totalCost / totals.budget) * 100 : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardHat className="h-5 w-5 text-primary" />
            {t("pages.reports.common.filters")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label>{t("pages.reports.projects.filters.client")}</Label>
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.reports.common.all")}</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.reports.projects.filters.contract", "Contrato")}</Label>
              <Select value={contractFilter} onValueChange={setContractFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.reports.common.all")}</SelectItem>
                  {visibleContracts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.number} — {c.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.reports.projects.filters.project")}</Label>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.reports.common.all")}</SelectItem>
                  {filteredProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.code} – {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={load} disabled={loading} className="w-full">
                <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                {t("pages.reports.common.refresh")}
              </Button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              disabled={!data || exporting !== null}
            >
              <FileSpreadsheet className="mr-1.5 h-4 w-4" />
              {t("pages.reports.common.exportCsv")}
            </Button>
            <Button size="sm" onClick={handleExportPdf} disabled={!data || exporting !== null}>
              {exporting === "pdf" ? (
                <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-1.5 h-4 w-4" />
              )}
              {t("pages.reports.common.exportPdf")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      ) : data ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              icon={<Wallet className="h-4 w-4" />}
              label={t("pages.reports.projects.kpis.totalBudget")}
              value={formatMoney(totals?.budget ?? 0, currency)}
            />
            <KpiCard
              icon={<Clock className="h-4 w-4" />}
              label={t("pages.reports.projects.kpis.labor")}
              value={formatMoney(totals?.laborCost ?? 0, currency)}
              hint={`${formatNumber(totals?.laborHours ?? 0, { maximumFractionDigits: 1 })}h`}
            />
            <KpiCard
              icon={<Package className="h-4 w-4" />}
              label={t("pages.reports.projects.kpis.materials")}
              value={formatMoney(totals?.materialsCost ?? 0, currency)}
            />
            <KpiCard
              icon={<Receipt className="h-4 w-4" />}
              label={t("pages.reports.projects.kpis.totalCost")}
              value={formatMoney(totals?.totalCost ?? 0, currency)}
              hint={t("pages.reports.projects.kpis.totalCostHint", {
                pct: totalUtilization.toFixed(0),
              })}
              tone={totalUtilization > 100 ? "danger" : totalUtilization > 80 ? "warn" : "success"}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t("pages.reports.projects.costsByProject")}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {data.rows.length === 0 ? (
                <p className="p-8 text-center text-sm text-muted-foreground">
                  {t("pages.reports.projects.noProjects")}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("pages.reports.projects.tableHeaders.project")}</TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.projects.tableHeaders.budget")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.projects.tableHeaders.labor")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.projects.tableHeaders.materials")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.projects.tableHeaders.expenses")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.projects.tableHeaders.totalCost")}
                      </TableHead>
                      <TableHead>{t("pages.reports.projects.tableHeaders.utilization")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.rows.map((r) => {
                      const utilPct = r.utilization * 100;
                      const overBudget = r.variance < 0;
                      return (
                        <TableRow key={r.projectId}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div>
                                <div className="text-xs font-mono text-muted-foreground">
                                  {r.code}
                                </div>
                                <div className="font-medium">{r.name}</div>
                                {r.client && (
                                  <div className="text-xs text-muted-foreground">{r.client}</div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney(r.budget, currency)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney(r.laborCost, currency)}
                            <div className="text-xs text-muted-foreground">
                              {formatNumber(r.laborHours, { maximumFractionDigits: 1 })}h
                            </div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney(r.materialsCost, currency)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney(r.expensesCost, currency)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="font-semibold tabular-nums">
                              {formatMoney(r.totalCost, currency)}
                            </div>
                            <div
                              className={`text-xs tabular-nums ${overBudget ? "text-destructive" : "text-emerald-600 dark:text-emerald-400"}`}
                            >
                              {overBudget
                                ? `${t("pages.reports.projects.above")} `
                                : `${t("pages.reports.projects.below")} `}
                              {formatMoney(Math.abs(r.variance), currency)}
                            </div>
                          </TableCell>
                          <TableCell className="min-w-[120px]">
                            {r.budget > 0 ? (
                              <div className="space-y-1">
                                <Progress value={Math.min(utilPct, 100)} className="h-1.5" />
                                <Badge
                                  variant={
                                    utilPct > 100
                                      ? "destructive"
                                      : utilPct > 80
                                        ? "secondary"
                                        : "outline"
                                  }
                                  className="text-[10px]"
                                >
                                  {utilPct.toFixed(0)}%
                                </Badge>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                {t("pages.reports.projects.noBudget")}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}

      <PdfPreviewDialog
        open={pdfPreviewOpen}
        onOpenChange={setPdfPreviewOpen}
        pdfBase64={pdfPreviewBase64}
        filename={pdfPreviewFilename}
        onDownload={confirmDownloadPdf}
      />
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  tone?: "success" | "warn" | "danger";
}) {
  const toneClass =
    tone === "success"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : tone === "danger"
          ? "text-destructive"
          : "text-foreground";
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          {icon}
          {label}
        </div>
        <div className={`mt-2 text-2xl font-bold tabular-nums ${toneClass}`}>{value}</div>
        {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}
